import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { BlockOutlined, CalendarMonth, Delete, Edit, MoreVert, Visibility } from '@mui/icons-material';
import { Menu, MenuItem } from '@mui/material';

import { CustomButton, CustomCheckBox, SearchSelect, Text } from '../../shared/uiComponents';
import CustomAlert from '../../shared/uiComponents/Alert';
import { Container, Loader } from '../../components/StyledComponents';
import { TEXT } from '../../shared/Helpers/colors';
import { DispatchProperties, useSelector } from '../../redux/store';
import { clearOverview, deleteRecurringSchedule, getScheduleOverview, updateSchedule } from '../../redux/State/clientSlice/recurringScheduleSlice';
import { getPendingRequests } from '../../redux/State/clientSlice/scheduleCancellationSlice';
import { AdminTypes } from '../../components/Menu';
import { ClientScheduleOverviewViewModel, RecurringScheduleViewModel, UpdateRecurringSchedulePayload } from '../../redux/API/ClientAPIHelpers/recurringSchedule';
import { errorNormalizer, getClientDisplayLabel, getInitials } from '../../shared/Helpers/functions';
import { DEFAULT_TIMEZONE_ID } from '../../shared/Helpers/timezones';
import CreateScheduleModal from './CreateScheduleModal';
import EditScheduleModal from './EditScheduleModal';
import RequestCancellationModal from './RequestTimeOffModal';
import PendingRequestsModal from './PendingRequestsModal';
import { useClientSearch } from './useClientSearch';
import { useProviderSearch } from './useProviderSearch';
import { WEEKDAYS } from './CreateScheduleModal/constants';
import { formatMinutesToTime } from './CreateScheduleModal/helpers';
import './styles.scss';

export const timeSlotHours = (startTimeMinutes: number, endTimeMinutes: number): number =>
	Math.round(((endTimeMinutes - startTimeMinutes) / 60) * 100) / 100;

export const scheduleHoursPerWeek = (schedule: RecurringScheduleViewModel): number =>
	Math.round(schedule.timeSlots.reduce((total, slot) => total + timeSlotHours(slot.startTimeMinutes, slot.endTimeMinutes), 0) * 100) / 100;

const formatHourLabel = (hours: number): string =>
	`${hours} ${hours === 1 ? 'hour' : 'hours'}`;

const formatShortHourLabel = (hours: number): string =>
	`${hours} ${hours === 1 ? 'hr' : 'hrs'}`;

export const weekdayList = (days: number[]): string =>
	[...days].sort((a, b) => a - b).map((day) => WEEKDAYS[day]?.long ?? '?').join(', ');

export interface ScheduleTimeGroup {
	days: number[];
	endTimeMinutes: number;
	startTimeMinutes: number;
}

export const scheduleTimeGroups = (schedule: RecurringScheduleViewModel): ScheduleTimeGroup[] => {
	const groups = new Map<string, ScheduleTimeGroup>();

	schedule.timeSlots.forEach(({ dayOfWeek, endTimeMinutes, startTimeMinutes }) => {
		const timeRange = `${startTimeMinutes}-${endTimeMinutes}`;
		const group = groups.get(timeRange) ?? { days: [], endTimeMinutes, startTimeMinutes };

		if (!group.days.includes(dayOfWeek)) group.days.push(dayOfWeek);

		groups.set(timeRange, group);
	});

	return Array.from(groups.values())
		.map((group) => ({ ...group, days: [...group.days].sort((a, b) => a - b) }))
		.sort(
			(a, b) => a.days[0] - b.days[0] || a.startTimeMinutes - b.startTimeMinutes || a.endTimeMinutes - b.endTimeMinutes,
		);
};

export const timeRangeLabel = ({ endTimeMinutes, startTimeMinutes }: ScheduleTimeGroup): string =>
	`${formatMinutesToTime(startTimeMinutes)} - ${formatMinutesToTime(endTimeMinutes)} (${formatHourLabel(timeSlotHours(startTimeMinutes, endTimeMinutes))} per session)`;

const isScheduleEnded = (schedule: RecurringScheduleViewModel): boolean => {
	const endDate = schedule.effectiveEndDate ?? schedule.endDate;
	if (!endDate) return false;

	return dayjs(endDate).format('YYYY-MM-DD') < dayjs().tz(DEFAULT_TIMEZONE_ID).format('YYYY-MM-DD');
};

const Schedules = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { overview, loading } = useSelector((state) => state.recurringSchedule);
	const pendingRequests = useSelector((state) => state.scheduleCancellation.pendingRequests);
	const role = useSelector((state) => state.account.role);
	const isAdmin = role?.section.id === AdminTypes.admin;
	const pendingScheduleRequestCount = pendingRequests.length;
	const cancellationWarningThreshold = 0;
	const [modalOpen, setModalOpen] = useState(false);
	const [cancellationTarget, setCancellationTarget] = useState<{
		clientId?: string;
		clientName?: string;
		providerId?: string;
		providerName?: string;
		recurringScheduleId?: string;
	} | null>(null);
	const [pendingModalOpen, setPendingModalOpen] = useState(false);
	const [includeEnded, setIncludeEnded] = useState(false);

	const [selectedClientId, setSelectedClientId] = useState('');
	const [selectedProviderId, setSelectedProviderId] = useState('');
	const { clientHasMore, clientSearch, setClientSearch, clientOptions, setClientPageSize } = useClientSearch(includeEnded);
	const { providerHasMore, providerSearch, setProviderSearch, providerOptions, setProviderPageSize } = useProviderSearch(includeEnded);

	// Menu & edit modal state
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const [activeSchedule, setActiveSchedule] = useState<RecurringScheduleViewModel | null>(null);
	const [activeAuthEndDate, setActiveAuthEndDate] = useState<string | null>(null);
	const [activeClientId, setActiveClientId] = useState<string>('');
	const [activeClientName, setActiveClientName] = useState<string>('');
	const [editOpen, setEditOpen] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

	const handleModalClose = useCallback(() => setModalOpen(false), []);

	useEffect(() => {
		if (isAdmin) {
			dispatch(getPendingRequests(undefined));
		}
	}, [dispatch, isAdmin]);

	// Only fetch schedules when at least one search filter is selected.
	const fetchOverview = useCallback(() => {
		if (!selectedClientId && !selectedProviderId) return;

		dispatch(getScheduleOverview({ clientId: selectedClientId, includeEnded, providerId: selectedProviderId }))
			.then(unwrapResult)
			.catch((error) => {
				if (error?.status === 403) navigate('/dashboard');
			});
	}, [dispatch, selectedClientId, includeEnded, selectedProviderId, navigate]);

	useEffect(() => {
		fetchOverview();
	}, [fetchOverview]);

	useEffect(() => {
		if (clientSearch.trim() || !selectedClientId) return;

		setSelectedClientId('');
		dispatch(clearOverview());
	}, [clientSearch, dispatch, selectedClientId]);

	useEffect(() => {
		if ((providerSearch || '').trim() || !selectedProviderId) return;

		setSelectedProviderId('');
		if (!selectedClientId) dispatch(clearOverview());
	}, [dispatch, providerSearch, selectedClientId, selectedProviderId]);

	const handleClientSelect = (clientId: string) => {
		setSelectedClientId(clientId);
		dispatch(clearOverview());
		const selected = clientOptions.find((o) => o.id === clientId);

		if (selected) setClientSearch(selected.label);
	};

	const handleProviderSelect = (providerId: string) => {
		setSelectedProviderId(providerId);
		dispatch(clearOverview());
	};

	const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>, schedule: RecurringScheduleViewModel, authorizationEndDate: string | null, clientId: string, clientName: string) => {
		setMenuAnchor(event.currentTarget);
		setActiveSchedule(schedule);
		setActiveAuthEndDate(authorizationEndDate);
		setActiveClientId(clientId);
		setActiveClientName(clientName);
	};

	const handleMenuClose = () => {
		setMenuAnchor(null);
	};

	const handleEditClick = () => {
		handleMenuClose();
		setEditOpen(true);
	};

	const handleDeleteClick = () => {
		handleMenuClose();
		setDeleteConfirmOpen(true);
	};

	const handleSetTimeOffClick = () => {
		handleMenuClose();
		setCancellationTarget({
			clientId: activeClientId,
			clientName: activeClientName,
			providerId: isAdmin && activeSchedule ? activeSchedule.providerId : undefined,
			providerName: isAdmin && activeSchedule ? activeSchedule.providerName : undefined,
			recurringScheduleId: activeSchedule ? activeSchedule.id : undefined,
		});
	};

	const handleEditSave = async (payload: UpdateRecurringSchedulePayload) => {
		if (!activeSchedule) return;

		try {
			await dispatch(updateSchedule({ id: activeSchedule.id, data: payload })).then(unwrapResult);
			setEditOpen(false);
			setActiveSchedule(null);
			fetchOverview();
		} catch (err) {
			const error = err as { errors?: Record<string, unknown>; title?: string; type?: string };

			if (error.type === 'TotalAuthorizationExceeded') {
				const proceed = window.confirm(
					`${error.title}\n\nDo you want to proceed anyway?`,
				);

				if (proceed) {
					try {
						await dispatch(updateSchedule({
							id: activeSchedule.id,
							data: { ...payload, acknowledgeTotalHoursWarning: true },
						})).then(unwrapResult);
						setEditOpen(false);
						setActiveSchedule(null);
						fetchOverview();
					} catch (retryErr) {
						errorNormalizer(retryErr as { errors: Record<string, unknown>; title: string });
					}
				}

				return;
			}

			errorNormalizer({ errors: error.errors, title: error.title });
		}
	};

	const handleDeleteConfirm = () => {
		if (!activeSchedule) return;

		dispatch(deleteRecurringSchedule(activeSchedule.id))
			.then(unwrapResult)
			.then(() => {
				setDeleteConfirmOpen(false);
				setActiveSchedule(null);
				fetchOverview();
			})
			.catch(errorNormalizer);
	};

	return (
    <Container>
      <div className="schedulePageHeader">
        <div className="schedulePageTitleBlock">
          <h1 className="schedulePageTitle">Client Schedules</h1>
          <p className="schedulePageSubtitle">
            Manage recurring schedules, track authorization usage, and oversee BT assignments for all clients
          </p>
        </div>
      </div>

      {pendingScheduleRequestCount > 0 && (
        <div className="pendingRequestsBanner">
          <div className="pendingRequestsContent">
            <span className="pendingRequestsIcon">ℹ</span>
            <div className="pendingRequestsText">
              <Text
                title={`${pendingScheduleRequestCount} Pending Schedule Request${pendingScheduleRequestCount === 1 ? '' : 's'}`}
                type="psm"
                fontWeight="semiBold"
              />
              <Text title="BTs have requested cancellations that need your review" type="pxs" color={TEXT.SECONDARY} />
            </div>
          </div>
          <CustomButton
            title="Review Requests"
            buttonType="primary"
            sizeType="sm"
            onClick={() => setPendingModalOpen(true)}
          />
        </div>
      )}

      <div className="scheduleToolbar">
        <div className="scheduleSearchRow">
          <div className="scheduleClientSearch">
            <SearchSelect
              label=""
              placeholder="Search clients with schedules..."
              data={clientOptions}
              hasMore={clientHasMore}
              searchString={clientSearch}
              setSize={setClientPageSize}
              setSearchString={setClientSearch}
              setValue={handleClientSelect}
            />
          </div>
          <div className="scheduleProviderSearch">
            <SearchSelect
              label=""
              placeholder="Search assigned providers..."
              data={providerOptions}
              hasMore={providerHasMore}
              searchString={providerSearch}
              setSize={setProviderPageSize}
              setSearchString={setProviderSearch}
              setValue={handleProviderSelect}
            />
          </div>
          <CustomCheckBox
            item={{ id: 'includeEnded', label: 'Include ended schedules', checked: includeEnded }}
            onChange={(e) => setIncludeEnded(e.target.checked)}
          />
        </div>
        <div className="scheduleToolbarActions">
          {!isAdmin && (
            <CustomButton
              title="Request Cancellation"
              buttonType="secondary"
              sizeType="sm"
              onClick={() => setCancellationTarget({})}
            />
          )}
          <CustomButton title="Create Schedule" buttonType="primary" sizeType="sm" onClick={() => setModalOpen(true)} />
        </div>
      </div>

      {!selectedClientId && !selectedProviderId && (
        <div className="emptyState">
          <Text
            title="Select a client with schedules or a provider assigned to schedules"
            type="pmd"
            color={TEXT.TERTIARY}
          />
        </div>
      )}

      {(selectedClientId || selectedProviderId) && loading && <Loader />}

      {(selectedClientId || selectedProviderId) && !loading && overview.length === 0 && (
        <div className="emptyState">
          <Text title="No schedules found for the selected filters" type="plg" color={TEXT.SECONDARY} />
          <Text title="Create a schedule to get started." type="pmd" color={TEXT.TERTIARY} />
        </div>
      )}

      {(selectedClientId || selectedProviderId) && !loading && (
        <div className="scheduleOverviewList">
          {overview.map((client: ClientScheduleOverviewViewModel) => {
            const adherenceActualSessionsToDate = client.adherenceActualSessionsToDate;
            const adherenceScheduledSessionsToDate = client.adherenceScheduledSessionsToDate;
            const activeCount = client.schedules.filter((s) => !isScheduleEnded(s)).length;
            const endedCount = client.schedules.length - activeCount;
            const clientLabel = getClientDisplayLabel(
              { fullName: client.clientName, isElc: client.isElc, dateOfBirthday: client.dateOfBirth },
              { shorten: true },
            );

            const hoursLabel = client.authorizationDetails.length > 0 && client.authorizationDetails.every((d) => !d.isForRbts)
              ? 'allocated' : 'authorized';
            // The authorized/week figure only reflects Weekly-frequency details (matching what the
            // system enforces per week). When a client is authorized purely on an Entire Period basis
            // that figure is 0, so fall back to the enforced period-total budget instead of showing "/0".
            const periodAuthorizedHours = Math.round(
              client.authorizationDetails.reduce((sum, d) => sum + (d.authorizedTotalHours || 0), 0) * 10) / 10;
            const periodScheduledHours = Math.round(
              client.authorizationDetails.reduce((sum, d) => sum + (d.scheduledTotalHours || 0), 0) * 10) / 10;
            const showPeriodTotal = client.authorizedHoursPerWeek <= 0 && periodAuthorizedHours > 0;

            return (
              <div key={client.clientId} className="clientScheduleCard">
                <div className="clientCardHeader">
                  <div className="clientAvatar">{getInitials(client.clientName)}</div>
                  <div className="clientInfo">
                    <Text
                      title={clientLabel}
                      type="pmd"
                      fontWeight="semiBold"
                    />
                    <div className="clientSubtitle">
                      {activeCount > 0 ? (
                        <span className="activeScheduleBadge">
                          <span className="activeScheduleDot" />
                          {activeCount} Active Schedule{activeCount === 1 ? '' : 's'}
                        </span>
                      ) : endedCount > 0 ? (
                        <span className="endedScheduleBadge">
                          <span className="endedScheduleDot" />
                          {endedCount} Ended Schedule{endedCount === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span className="noScheduleBadge">
                          <CalendarMonth style={{ fontSize: 14 }} />
                          No Schedules
                        </span>
                      )}
                      <span className="subtitleSeparator">•</span>
                      <span className="hoursScheduledText">
                        {showPeriodTotal
                          ? `${periodScheduledHours} / ${periodAuthorizedHours} ${hoursLabel} hrs over period scheduled`
                          : `${client.scheduledHoursPerWeek} / ${client.authorizedHoursPerWeek} ${hoursLabel} hrs/wk scheduled`}
                      </span>
                    </div>
                  </div>
                  <button
                    className="viewButton"
                    type="button"
                    aria-label="View client"
                    onClick={() => navigate(`/clients/client/${client.clientId}`)}
                  >
                    <Visibility fontSize="small" />
                  </button>
                </div>

                <div className="statsRow">
                  <div className="statColumn">
                    <span className="statLabel">Schedule Adherence (This Month)</span>
                    <div className="statValue">
                      <div className="statInlineValue">
                        <span
                          className={`statNumber ${adherenceScheduledSessionsToDate < 5 ? '' : client.scheduleAdherencePercent >= 80 ? 'statNumberSuccess' : client.scheduleAdherencePercent >= 50 ? 'statNumberWarning' : 'statNumberDanger'}`}
                        >
                          {adherenceScheduledSessionsToDate > 0 ? `${client.scheduleAdherencePercent}%` : '—'}
                        </span>
                        {adherenceScheduledSessionsToDate > 0 && (
                          <span className="statNumberUnit">
                            ({adherenceActualSessionsToDate}/{adherenceScheduledSessionsToDate} sessions)
                          </span>
                        )}
                      </div>
                      <span className="statDetail statDetailSecondary">
                        {client.adherenceScheduledSessionsThisMonth} sessions scheduled
                      </span>
                    </div>
                  </div>
                  <div className="statColumn">
                    <span className="statLabel">Hours Utilized (This Month)</span>
                    <div className="statValue">
                      <div className="statInlineValue">
                        <span className="statNumber">
                          {client.hoursUtilizedThisMonth} / {client.hoursScheduledThisMonth}
                        </span>
                        <span className="statNumberUnit">hrs utilized</span>
                      </div>
                      <span className="statDetail statDetailSecondary">
                        {formatShortHourLabel(client.hoursScheduledThisMonth)} scheduled
                      </span>
                    </div>
                  </div>
                  <div className="statColumn">
                    <span className="statLabel">Sessions Cancelled (Last 30 Days)</span>
                    <div className="statValue">
                      <span
                        className={`statNumber ${client.cancellationsLast30Days > cancellationWarningThreshold ? 'statNumberWarning' : ''}`}
                      >
                        {client.cancellationsLast30Days}
                      </span>
                    </div>
                  </div>
                </div>

                {client.schedules.length === 0 ? (
                  <div className="noSchedulesEmpty">
                    <CalendarMonth style={{ fontSize: 40, color: 'rgba(0,0,0,0.2)' }} />
                    <Text title="No recurring sessions scheduled yet" type="psm" color={TEXT.TERTIARY} />
                    <CustomButton
                      title="Create Schedule"
                      buttonType="primary"
                      sizeType="sm"
                      onClick={() => setModalOpen(true)}
                    />
                  </div>
                ) : (
                  <div className="scheduleList">
                    {client.schedules.map((schedule) => {
                      const ended = isScheduleEnded(schedule);
                      const endDate = schedule.effectiveEndDate ?? schedule.endDate;
                      const timeGroups = scheduleTimeGroups(schedule);

                      return (
                        <div key={schedule.id} className={`scheduleItem ${ended ? 'scheduleItemEnded' : ''}`}>
                          <div className="scheduleItemRow1">
                            <span className={`frequencyBadge ${ended ? 'frequencyBadgeEnded' : ''}`}>
                              {ended ? 'Ended' : schedule.step > 1 ? 'Every Other Week' : 'Weekly'}
                            </span>
                            <span className="scheduleDays">
                              {weekdayList(schedule.daysOfWeek)}
                            </span>
                            <button
                              className="scheduleMenuBtn"
                              type="button"
                              aria-label="Schedule options"
                              onClick={(e) =>
                                handleMenuOpen(
                                  e,
                                  schedule,
                                  schedule.endDateLimit === undefined ? client.authorizationEndDate : schedule.endDateLimit,
                                  client.clientId,
                                  client.clientName,
                                )
                              }
                            >
                              <MoreVert fontSize="small" style={{ color: TEXT.TERTIARY }} />
                            </button>
                          </div>
                          <div className="scheduleItemRow2">
                            {timeGroups.map((group) => (
                              <div key={`${group.startTimeMinutes}-${group.endTimeMinutes}`}>
                                {timeGroups.length > 1 && `${weekdayList(group.days)}: `}
                                {timeRangeLabel(group)}
                              </div>
                            ))}
                            <div>{formatHourLabel(scheduleHoursPerWeek(schedule))} per week</div>
                          </div>
                          <div className="scheduleItemRow3">
                            <span className="scheduleMetaChip">
                              <span className="metaIcon metaIconSession" />
                              {schedule.sessionTypeName}
                            </span>
                          </div>
                          <div className="scheduleItemRow4">
                            <span className="metaIcon metaIconCalendar" />
                            {ended ? (
                              `${schedule.endDate ? 'Ended on' : 'Auth ended'} ${dayjs(endDate).format('MMM D, YYYY')}`
                            ) : (
                              <>
                                {schedule.startDate && `Started ${dayjs(schedule.startDate).format('MMM D, YYYY')}`}
                                {endDate && ` • ${schedule.endDate ? 'Ends' : 'Auth ends'} ${dayjs(endDate).format('MMM D, YYYY')}`}
                              </>
                            )}
                          </div>
                          <div className="scheduleProvider">
                            <span className="providerLabel">Assigned Provider</span>
                            <div className="providerRow">
                              <div className="providerAvatar">{getInitials(schedule.providerName)}</div>
                              <span className="providerName">{schedule.providerName}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEditClick} sx={{ gap: 1 }}>
          <Edit fontSize="small" sx={{ color: 'text.secondary' }} />
          Edit Schedule
        </MenuItem>
        <MenuItem onClick={handleSetTimeOffClick} sx={{ gap: 1 }}>
          <BlockOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
          Cancel Sessions for Date Range
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{  gap: 1 , color: '#ef4444' }}>
          <Delete fontSize="small" sx={{  color: '#ef4444'  }} />
          Delete Schedule
        </MenuItem>
      </Menu>

      {activeSchedule && (
        <CustomAlert
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title="Edit Schedule"
          maxWidth="xs"
          Content={() => (
            <EditScheduleModal
              authorizationEndDate={activeAuthEndDate}
              isAdmin={isAdmin}
              schedule={activeSchedule}
              onSave={handleEditSave}
              onClose={() => setEditOpen(false)}
            />
          )}
        />
      )}

      {activeSchedule && (
        <CustomAlert
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          title="Delete Schedule"
          maxWidth="xs"
          Content={() => (
            <div className="deleteConfirmContent">
              <Text
                title="Are you sure you want to delete this schedule? This action cannot be undone."
                type="psm"
                color={TEXT.SECONDARY}
              />
              <div className="deleteConfirmActions">
                <CustomButton title="Cancel" buttonType="secondary" onClick={() => setDeleteConfirmOpen(false)} />
                <CustomButton title="Delete" buttonType="primary" onClick={handleDeleteConfirm} />
              </div>
            </div>
          )}
        />
      )}

      <CreateScheduleModal open={modalOpen} onClose={handleModalClose} onCreated={fetchOverview} />

      <CustomAlert
        open={cancellationTarget !== null}
        onClose={() => setCancellationTarget(null)}
        title={cancellationTarget?.providerId ? 'Cancel Sessions for Date Range' : 'Request Cancellation'}
        maxWidth="sm"
        disableBackdropClose
        Content={() => (
          <RequestCancellationModal
            providerId={cancellationTarget?.providerId}
            providerName={cancellationTarget?.providerName}
            clientId={cancellationTarget?.clientId}
            clientName={cancellationTarget?.clientName}
            recurringScheduleId={cancellationTarget?.recurringScheduleId}
            onClose={() => setCancellationTarget(null)}
            onSuccess={() => {
              setCancellationTarget(null);
              fetchOverview();
            }}
          />
        )}
      />

      <CustomAlert
        open={pendingModalOpen}
        onClose={() => {
          setPendingModalOpen(false);
          dispatch(getPendingRequests(undefined));
        }}
        title="Pending Cancellation Requests"
        maxWidth="md"
        Icon={() => <span />}
        iconBackgroundColor="transparent"
        compactHeaderIcon
        Content={() => (
          <PendingRequestsModal
            onClose={() => {
              setPendingModalOpen(false);
              dispatch(getPendingRequests(undefined));
            }}
          />
        )}
      />
    </Container>
  );
};

export default Schedules;
